import React, { useCallback, useState } from 'react';

import debounce from 'debounce-promise';
import { Control, Controller, FieldErrors, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { MultiValue, SingleValue } from 'react-select';

import Input from 'components/Inputs/Input';
import SelectCreatable from 'components/SelectCreatable';
import { Checkbox, CheckboxType } from 'components/Checkbox';
import { IncomeW2Type } from 'api/UnderwritingApi';
import { BorrowerCompanyVariable } from 'enums/BorrowerCompanyVariable';
import { BorrowerIncomeType, OtherIncomeTypes } from 'enums/BorrowerIncomeType';
import { DEFAULT_DEBOUNCE_TIMEOUT, INCOME_AMOUNT_MAX_LENGTH } from 'utils/constants';
import { convertInputSelectValue, OptionType } from 'utils/inputSelect';
import { maskMonetaryValue, removeNonDigits } from 'utils/stringUtils';
import { getMessageForRequiredFields } from 'utils/validationUtils';
import { preQualificationApi } from 'api/PreQualification/PreQualificationApi';
import { Company } from 'api/PreQualification/preQualificationApi.types';

import { ANNUAL_INCOME, BorrowerIncomesFormData, EMPLOYER_NAME } from './IncomeForm';
import styles from './IncomeForm.module.scss';

const W2_INCOME_LABEL = 'W2';
const W2_EMPLOYMENT_TYPE_LABEL = 'W2 Employment Type';

interface W2IncomeFormProps {
  watcher: Record<string, any>;
  type: BorrowerIncomeType | OtherIncomeTypes;
  caption: string;
  companyNameKey: BorrowerCompanyVariable;
  companyMappingIdKey: BorrowerCompanyVariable;
  incomeTypeKey: BorrowerCompanyVariable;
  control: Control<BorrowerIncomesFormData>;
  setValue: UseFormSetValue<BorrowerIncomesFormData>;
  errors: FieldErrors<BorrowerIncomesFormData>;
  trigger: UseFormTrigger<BorrowerIncomesFormData>;
  clearErrors: (name?: BorrowerCompanyVariable | BorrowerCompanyVariable[]) => void;
}

const W2IncomeForm = ({
  watcher,
  type,
  caption,
  companyNameKey,
  companyMappingIdKey,
  incomeTypeKey,
  control,
  setValue,
  trigger,
  errors,
  clearErrors,
}: W2IncomeFormProps) => {
  const [foundCompanies, setFoundCompanies] = useState<Company[]>([]);
  const [currentCompanyNameValue, setCurrentCompanyNameValue] = useState<string>('');

  const loadCompanies = useCallback(
    debounce(async (companyName: string) => {
      try {
        if (!!companyName.trim().length) {
          const { companies } = await preQualificationApi.getCompanies(companyName);

          setFoundCompanies(companies);

          return companies.map((company) => {
            return {
              value: convertInputSelectValue(company.companyName),
              label: company.companyName,
              iconUrl: company.logoUrl,
            } as OptionType;
          });
        } else {
          setFoundCompanies([]);
        }

        return [];
      } catch (error) {
        return [];
      }
    }, DEFAULT_DEBOUNCE_TIMEOUT),
    [],
  );

  const handleCompanyNameInputChange = (newInputValue: string) => {
    setCurrentCompanyNameValue(newInputValue);
  };

  const handleCompanyNameChange = (option: SingleValue<OptionType> | MultiValue<OptionType>) => {
    if (clearErrors) {
      clearErrors(companyNameKey);
    }

    if (!option) {
      setValue(companyNameKey, '');
      setValue(companyMappingIdKey, '');

      return;
    }

    if ((option as OptionType).__isNew__) {
      setValue(companyNameKey, (option as OptionType).label);

      return;
    } else {
      const selectedOption = foundCompanies.find((company) => company.companyName === (option as OptionType).label);

      if (selectedOption) {
        setValue(companyNameKey, (option as OptionType).label);
        setValue(companyMappingIdKey, selectedOption.companyMappingId);
      } else {
        setValue(companyNameKey, (option as OptionType).label);
      }
    }
  };

  const getCurrentValue = (value: string): OptionType => {
    if (!value) {
      return {
        value: '',
        label: '',
        iconUrl: '',
      };
    }

    const selectedCompany = foundCompanies.find((company) => company.companyName === value);

    if (!selectedCompany) {
      return {
        value: convertInputSelectValue(value),
        label: value,
        iconUrl: '',
      };
    }

    return {
      value: convertInputSelectValue(selectedCompany.companyName),
      label: selectedCompany.companyName,
      iconUrl: selectedCompany.logoUrl,
    };
  };

  const formatCreateLabel = (input: string) => {
    return <>I cannot find my company in the list. Add: {input}</>;
  };

  return (
    <div className={styles.w2IncomeContainer}>
      <Controller
        name={incomeTypeKey}
        control={control}
        shouldUnregister
        rules={{
          validate: (value) =>
            (!!value && watcher.selected.includes(type)) || getMessageForRequiredFields(W2_EMPLOYMENT_TYPE_LABEL),
        }}
        render={() => (
          <div className={styles.incomeTypeContainer}>
            <span className={styles.incomeTypeLabel}>{W2_EMPLOYMENT_TYPE_LABEL}</span>
            <div className={styles.incomeTypeOptions}>
              {Object.values(IncomeW2Type).map((option) => (
                <Checkbox
                  key={option}
                  name={incomeTypeKey}
                  type={CheckboxType.Primary}
                  isChecked={watcher[incomeTypeKey] === option}
                  text={option}
                  onChange={() => {
                    setValue(incomeTypeKey, option);
                    trigger(incomeTypeKey);
                  }}
                />
              ))}
            </div>
            {errors[incomeTypeKey] && <p className={styles.incomeTypeError}>{errors[incomeTypeKey]?.message}</p>}
          </div>
        )}
      />
      <div className={styles.incomeContainer}>
        <Controller
          name={companyNameKey}
          control={control}
          shouldUnregister
          rules={{
            validate: (value) => {
              return (!!value && watcher.selected.includes(type)) || getMessageForRequiredFields(EMPLOYER_NAME);
            },
          }}
          render={() => (
            <SelectCreatable
              name={type}
              isClearable
              isOptionWithIcon
              control={control}
              isValidNewOption={() => !!currentCompanyNameValue.length}
              loadOptions={loadCompanies}
              label={EMPLOYER_NAME}
              formatCreateLabel={formatCreateLabel}
              onChange={handleCompanyNameChange}
              onInputChange={handleCompanyNameInputChange}
              value={getCurrentValue(watcher[companyNameKey])}
              inputValue={currentCompanyNameValue}
              inputContainerClassname={styles.input}
              errorMessage={errors && errors[companyNameKey]?.message}
            />
          )}
        />
        <Controller
          name={type}
          control={control}
          shouldUnregister
          rules={{
            validate: (value) => {
              return (
                (Number(removeNonDigits(value)) && watcher.selected.includes(type)) ||
                getMessageForRequiredFields(ANNUAL_INCOME)
              );
            },
          }}
          render={() => (
            <Input
              name={type}
              label={W2_INCOME_LABEL}
              className={styles.input}
              value={maskMonetaryValue(watcher[type]!)}
              maxLength={INCOME_AMOUNT_MAX_LENGTH}
              errorMessage={errors[type]?.message}
              caption={caption}
              inputMode="numeric"
              onChange={(event) => {
                setValue(type, removeNonDigits(event.target.value));
                trigger(type);
              }}
            />
          )}
        />
      </div>
    </div>
  );
};

export default W2IncomeForm;
